import { Direction, Position, TilePos, TileType } from '../types';
import { MAZE_WIDTH, MAZE_HEIGHT, PLAYER_START } from '../maps/levels';
import { isWalkableForPlayer } from '../maps/tileTypes';

export class Player {
  pos: Position;
  tilePos: TilePos;
  direction: Direction = Direction.None;
  nextDirection: Direction = Direction.None;
  speed: number;
  animFrame: number = 0;
  animTimer: number = 0;
  moving: boolean = false;

  private tileSize: number;

  constructor(tileSize: number, speed: number) {
    this.tileSize = tileSize;
    this.speed = speed;
    this.tilePos = { ...PLAYER_START };
    this.pos = {
      x: PLAYER_START.col * tileSize + tileSize / 2,
      y: PLAYER_START.row * tileSize + tileSize / 2,
    };
  }

  reset(): void {
    this.tilePos = { ...PLAYER_START };
    this.pos = {
      x: PLAYER_START.col * this.tileSize + this.tileSize / 2,
      y: PLAYER_START.row * this.tileSize + this.tileSize / 2,
    };
    this.direction = Direction.None;
    this.nextDirection = Direction.None;
    this.moving = false;
    this.animFrame = 0;
  }

  update(dt: number, maze: number[][], queuedDir: Direction): boolean {
    let directionChanged = false;

    if (queuedDir !== Direction.None) {
      this.nextDirection = queuedDir;
    }

    const centerX = this.tilePos.col * this.tileSize + this.tileSize / 2;
    const centerY = this.tilePos.row * this.tileSize + this.tileSize / 2;
    const moveThisFrame = this.speed * dt;
    const threshold = Math.max(moveThisFrame, 0.5);
    const atCenter =
      Math.abs(this.pos.x - centerX) < threshold &&
      Math.abs(this.pos.y - centerY) < threshold;

    if (atCenter) {
      this.pos.x = centerX;
      this.pos.y = centerY;

      if (this.nextDirection !== Direction.None && this.canMove(this.nextDirection, maze)) {
        this.direction = this.nextDirection;
        this.nextDirection = Direction.None;
        directionChanged = true;
      }

      if (!this.canMove(this.direction, maze)) {
        this.moving = false;
        return directionChanged;
      }
    }

    if (this.direction === Direction.None) {
      this.moving = false;
      return directionChanged;
    }

    this.moving = true;
    const dist = this.speed * dt;
    const { dx, dy } = this.directionDelta(this.direction);
    this.pos.x += dx * dist;
    this.pos.y += dy * dist;

    // Tunnel wrap-around
    if (this.pos.x < -this.tileSize / 2) {
      this.pos.x = MAZE_WIDTH * this.tileSize - this.tileSize / 2;
    } else if (this.pos.x > MAZE_WIDTH * this.tileSize - this.tileSize / 2) {
      this.pos.x = -this.tileSize / 2;
    }

    this.tilePos = {
      col: Math.floor(this.pos.x / this.tileSize),
      row: Math.floor(this.pos.y / this.tileSize),
    };

    this.tilePos.col = Math.max(0, Math.min(MAZE_WIDTH - 1, this.tilePos.col));
    this.tilePos.row = Math.max(0, Math.min(MAZE_HEIGHT - 1, this.tilePos.row));

    this.animTimer += dt;
    if (this.animTimer > 0.08) {
      this.animTimer = 0;
      this.animFrame = (this.animFrame + 1) % 4;
    }

    return directionChanged;
  }

  private canMove(dir: Direction, maze: number[][]): boolean {
    const { dx, dy } = this.directionDelta(dir);
    let nextCol = this.tilePos.col + dx;
    let nextRow = this.tilePos.row + dy;

    // Tunnel
    if (nextCol < 0) nextCol = MAZE_WIDTH - 1;
    if (nextCol >= MAZE_WIDTH) nextCol = 0;
    if (nextRow < 0 || nextRow >= MAZE_HEIGHT) return false;

    return isWalkableForPlayer(maze[nextRow][nextCol] as TileType);
  }

  private directionDelta(dir: Direction): { dx: number; dy: number } {
    switch (dir) {
      case Direction.Up: return { dx: 0, dy: -1 };
      case Direction.Down: return { dx: 0, dy: 1 };
      case Direction.Left: return { dx: -1, dy: 0 };
      case Direction.Right: return { dx: 1, dy: 0 };
      default: return { dx: 0, dy: 0 };
    }
  }
}
